/**
 * 앱 링크 표(AC0-261): Android App Links · iOS Universal Links · 커스텀 스킴 딥링크가 같은 값을 본다.
 * 런타임(deepLink.ts)과 빌드(vite.config.ts → dist/.well-known/*)가 함께 import 하므로 브라우저·노드 전용 API 를 쓰지 않는다.
 * 네이티브 쪽 같은 값: android/app/build.gradle flavor `appLinkHost` · AndroidManifest intent-filter,
 * ios/App/Config/{Dev,Prod}.xcconfig `HAMLAK_APP_LINK_HOST` · App.entitlements associated-domains.
 */

export type LinkStage = "dev" | "prod";

/** Apple Developer 팀 ID(공개 값, ios/App/Config/*.xcconfig DEVELOPMENT_TEAM 과 같은 값). */
export const APPLE_TEAM_ID = "323VSWGR34";

export interface StageLinks {
  /** 웹 호스트 = 앱 링크 도메인(infra/lib/config.ts host). */
  host: string;
  /** Android applicationId(build.gradle flavor). */
  androidPackage: string;
  /** iOS 번들 ID(xcconfig PRODUCT_BUNDLE_IDENTIFIER). */
  iosBundleId: string;
  /**
   * assetlinks.json 의 SHA-256 서명 인증서 지문(`AA:BB:…` 대문자 콜론 구분). 공개 값이지만 추측 금지 — Play Console
   * [설정 → 앱 무결성 → 앱 서명] 의 "앱 서명 키 인증서" SHA-256(필수)과 "업로드 키 인증서" SHA-256(사이드로드 release 확인용)을 넣는다.
   * 비어 있으면 그 스테이지 빌드는 assetlinks.json 을 만들지 않는다(빌드 경고) → Android App Link 검증 실패, 커스텀 스킴만 동작.
   */
  androidSha256: string[];
}

export const STAGE_LINKS: Record<LinkStage, StageLinks> = {
  dev: {
    host: "dev.hamlak.com",
    androidPackage: "com.sl.hamlak.dev",
    iosBundleId: "com.sl.hamlak.dev",
    androidSha256: [
      // Play Console 'hamlak dev'(com.sl.hamlak.dev) 앱 서명 키 SHA-256 (10/28 Jaden)
      "B7:3D:72:67:F6:74:D2:0E:8F:95:BD:52:39:69:F4:20:64:AA:82:7D:31:65:78:0B:8E:05:D4:0A:40:15:34:BB",
    ],
  },
  prod: {
    host: "hamlak.com",
    androidPackage: "com.sl.hamlak",
    iosBundleId: "com.sl.hamlak",
    androidSha256: [
      // Play Console 'hamlak'(com.sl.hamlak) 앱 서명 키 SHA-256 (10/28 Jaden)
      "BA:F7:26:3B:1A:1A:3C:43:39:31:15:9C:F9:89:A4:BF:48:53:95:1A:FD:61:D7:43:0A:1A:B2:5B:2C:C5:3E:3D",
    ],
  },
};

/** 앱 링크로 받을 웹 호스트. www 는 301 로 넘어가므로 검증 대상은 아니지만 링크로 들어오면 받는다. */
export const APP_LINK_HOSTS: readonly string[] = [STAGE_LINKS.prod.host, `www.${STAGE_LINKS.prod.host}`, STAGE_LINKS.dev.host];

/**
 * 앱이 가로챌 https 경로(AASA components · AndroidManifest data 와 같은 목록). `*` = 한 조각 이상.
 * `/store` 는 정확히 일치만 — `/store/web/success|fail`(토스 결제 복귀, 웹 전용)은 브라우저에 남긴다. `/api/*` 는 넣지 않는다.
 */
export const APP_LINK_PATHS = ["/plots/*", "/chat/*", "/home", "/store", "/go"] as const;

const FINGERPRINT = /^[0-9A-F]{2}(:[0-9A-F]{2}){31}$/;

/** `/.well-known/assetlinks.json`. 지문이 없으면 null(파일을 만들지 않는다). 형식이 틀리면 throw. */
export function buildAssetLinks(stage: LinkStage): string | null {
  const s = STAGE_LINKS[stage];
  if (s.androidSha256.length === 0) return null;
  const bad = s.androidSha256.filter((f) => !FINGERPRINT.test(f));
  if (bad.length > 0) throw new Error(`appLinks: ${stage} androidSha256 형식 오류(AA:BB:… 32바이트 대문자): ${bad.join(", ")}`);
  const statements = [
    {
      relation: ["delegate_permission/common.handle_all_urls"],
      target: { namespace: "android_app", package_name: s.androidPackage, sha256_cert_fingerprints: s.androidSha256 },
    },
  ];
  return `${JSON.stringify(statements, null, 2)}\n`;
}

/** `/.well-known/apple-app-site-association`(확장자 없음, application/json). iOS 13+ components 형식. */
export function buildAppleAppSiteAssociation(stage: LinkStage): string {
  const s = STAGE_LINKS[stage];
  const aasa = {
    applinks: {
      details: [{ appIDs: [`${APPLE_TEAM_ID}.${s.iosBundleId}`], components: APP_LINK_PATHS.map((p) => ({ "/": p })) }],
    },
  };
  return `${JSON.stringify(aasa, null, 2)}\n`;
}
